import { Bijection, bijectionCompose } from "@redotech/util/bijection";
import { utf8Encoding } from "@redotech/util/encoding";
import { KeyFn } from "@redotech/util/key";
import { StringFormat } from "@redotech/util/string-format";

export type Json =
  | JsonArray
  | JsonBoolean
  | JsonObject
  | JsonNumber
  | JsonNull
  | JsonString
  | {};

export interface JsonArray extends Array<Json> {}

export type JsonBoolean = boolean;

export interface JsonObject {
  [key: JsonString]: Json;
}

// This is just to satisfy the type checker.
// Empty objects are valid JsonObjects but the type checker want there to be some key in there to make sure its a string and not something else.
export type JsonObjectWithEmpty = JsonObject | {};

export type JsonNumber = number;

export type JsonNull = null;

export type JsonString = string;

export const jsonStringFormat: StringFormat<Json> = {
  read(string: string) {
    return JSON.parse(string);
  },
  write(value: Json) {
    return JSON.stringify(value);
  },
};

const tryReadJsonUnicodeEscape = (
  json: string,
  index: number,
): number | undefined => {
  if (json.charAt(index) !== "\\" || json.charAt(index + 1) !== "u") {
    return undefined;
  }

  let precedingBackslashes = 0;
  for (
    let cursor = index - 1;
    cursor >= 0 && json.charAt(cursor) === "\\";
    cursor--
  ) {
    precedingBackslashes++;
  }
  if (precedingBackslashes % 2 === 1) {
    return undefined;
  }

  const hex = json.slice(index + 2, index + 6);
  return /^[0-9a-fA-F]{4}$/.test(hex) ? Number.parseInt(hex, 16) : undefined;
};

const isHighSurrogate = (code: number) => code >= 0xd8_00 && code <= 0xdb_ff;

const isLowSurrogate = (code: number) => code >= 0xdc_00 && code <= 0xdf_ff;

export function stringifyJsonForJsonb(value: unknown): string {
  const json = JSON.stringify(value) ?? "null";
  if (!/\\u[dD][89a-fA-F][0-9a-fA-F]{2}/.test(json)) {
    return json;
  }

  let result = "";
  for (let index = 0; index < json.length; ) {
    const code = tryReadJsonUnicodeEscape(json, index);
    if (code === undefined) {
      result += json.charAt(index);
      index++;
      continue;
    }

    if (isHighSurrogate(code)) {
      const nextCode = tryReadJsonUnicodeEscape(json, index + 6);
      if (nextCode !== undefined && isLowSurrogate(nextCode)) {
        result += json.slice(index, index + 12);
        index += 12;
      } else {
        result += "\\ufffd";
        index += 6;
      }
      continue;
    }

    if (isLowSurrogate(code)) {
      result += "\\ufffd";
      index += 6;
      continue;
    }

    result += json.slice(index, index + 6);
    index += 6;
  }
  return result;
}

export const jsonBufferFormat: Bijection<ArrayBuffer, Json> = bijectionCompose(
  utf8Encoding,
  jsonStringFormat,
);

export const jsonKeyFn: KeyFn<Json> = jsonStringFormat.write;

/**
 * Result of JSON.parse(JSON.stringify(t))
 */
export type Jsonified<T> = T extends { toJSON(): infer R }
  ? R
  : T extends string | number | boolean | null
    ? T
    : T extends (infer E)[]
      ? Jsonified<E>[]
      : T extends (...args: never) => unknown
        ? never
        : T extends object
          ? { [K in keyof T]: Jsonified<T[K]> }
          : never;
